import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeSlash, CheckCircle, ArrowUp, CreditCard, CalendarBlank, ArrowsClockwise, XCircle, Receipt, Lightning, ShieldCheck } from '@phosphor-icons/react';
import { EcowittSettingsCard } from '../components/EcowittSettingsCard';
import { MorningBriefCard } from '../components/MorningBriefCard';
import { FoundingBadge } from '../components/FoundingBadge';
import { ApiTokensCard } from '../components/ApiTokensCard';
import { WebhooksCard } from '../components/WebhooksCard';
import { PasskeySettingsCard } from '../components/PasskeySettingsCard';
import { ComplianceGuardCard } from '../components/ComplianceGuardCard';
import { VacationModeCard } from '../components/VacationModeCard';
import { InnerCircleCTA } from '../components/InnerCircleCTA';
import { Crown } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// Upgrade confirmation dialog
const UpgradeDialog = ({ isOpen, onClose, onConfirm, loading, currentTier }) => {
  if (!isOpen) return null;
  
  const isUpgradeToAnnual = currentTier === 'homeowner_seasonal';
  const title = isUpgradeToAnnual ? 'Upgrade to Enthusiast Annual' : 'Subscribe to Enthusiast Annual';
  const priceText = isUpgradeToAnnual ? 'Upgrade Now - $80/year' : 'Subscribe - $80/year';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-muted-foreground mb-4">
          {isUpgradeToAnnual 
            ? "You're about to upgrade from Homeowner Seasonal ($50/season) to Enthusiast Annual ($80/year)."
            : "Subscribe to Enthusiast Annual for full access to all TurfPulse features."
          }
        </p>
        <div className="bg-muted/50 rounded-lg p-4 mb-4">
          <h4 className="font-semibold mb-2">What you'll get:</h4>
          <ul className="text-sm space-y-1 text-muted-foreground">
            <li>• Full year of access (auto-renews annually)</li>
            <li>• All 4 GDD departments (PGR, Weed, Pest, Disease)</li>
            <li>• Soil Analysis & 5-Day Spray Forecast</li>
            <li>• Unlimited zones & priority support</li>
            <li>• Cancel anytime from your profile</li>
          </ul>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          <ArrowsClockwise size={14} className="inline mr-1" />
          Auto-renews annually. Cancel anytime via subscription management.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} className="flex-1" disabled={loading}>
            {loading ? 'Processing...' : priceText}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Cancel subscription confirmation dialog
const CancelDialog = ({ isOpen, onClose, onConfirm, loading, expiresDate }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-xl font-bold mb-2 text-destructive">Cancel Subscription?</h3>
        <p className="text-muted-foreground mb-4">
          Are you sure you want to cancel your TurfPulse subscription?
        </p>
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>You'll keep access until:</strong><br />
            {expiresDate ? new Date(expiresDate).toLocaleDateString('en-US', { 
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
            }) : 'End of billing period'}
          </p>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          After cancellation, you can still reactivate anytime before your access expires.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
            Keep Subscription
          </Button>
          <Button variant="destructive" onClick={onConfirm} className="flex-1" disabled={loading}>
            {loading ? 'Canceling...' : 'Yes, Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation(['profile', 'common', 'nav']);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [analyticsOptOut, setAnalyticsOptOut] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Fetch subscription details on mount
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/payments/subscription-details`, {
          withCredentials: true
        });
        setSubscriptionDetails(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription details:', error);
      }
    };
    
    if (user) {
      fetchSubscriptionDetails();
      // Fetch analytics opt-out status
      axios
        .get(`${API_URL}/api/analytics/opt-out-status`, { withCredentials: true })
        .then((r) => setAnalyticsOptOut(!!r.data?.analytics_opt_out))
        .catch(() => {});
    }
  }, [user]);

  const handleAnalyticsToggle = async (nextOptedOut) => {
    setAnalyticsLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/analytics/opt-out`,
        { analytics_opt_out: nextOptedOut },
        { withCredentials: true }
      );
      setAnalyticsOptOut(nextOptedOut);
      toast.success(
        nextOptedOut
          ? 'Analytics turned off. Existing data for your account was purged.'
          : 'Thanks — your usage helps us make TurfPulse better.'
      );
    } catch (e) {
      toast.error('Could not update preference. Try again.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setUpgradeLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/subscribe`, {
        tier: 'enthusiast_annual'
      }, { withCredentials: true });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate subscription');
    } finally {
      setUpgradeLoading(false);
      setShowUpgradeDialog(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/cancel-subscription`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message || 'Subscription canceled');
      setShowCancelDialog(false);
      // Refresh subscription details
      const detailsRes = await axios.get(`${API_URL}/api/payments/subscription-details`, {
        withCredentials: true
      });
      setSubscriptionDetails(detailsRes.data);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setUpgradeLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/reactivate-subscription`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message || 'Subscription reactivated!');
      // Refresh subscription details
      const detailsRes = await axios.get(`${API_URL}/api/payments/subscription-details`, {
        withCredentials: true
      });
      setSubscriptionDetails(detailsRes.data);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/customer-portal`, {}, {
        withCredentials: true
      });
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const tierDisplay = user?.subscription_tier === 'founding' ? 'Founding Member'
    : user?.subscription_tier === 'enthusiast_annual' ? 'Enthusiast Annual'
    : user?.subscription_tier === 'homeowner_seasonal' ? 'Homeowner Seasonal'
    : 'Free';

  const isPremium = user?.subscription_tier && user?.subscription_tier !== 'free';
  const isCanceling = subscriptionDetails?.cancel_at_period_end || subscriptionDetails?.status === 'canceling';

  return (
    <div data-testid="profile-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <main className="max-w-2xl mx-auto p-6 space-y-6">
        <h1 className="text-2xl font-bold">{t('profile:title')}</h1>

        {/* Founding Member badge — self-hides for non-founders */}
        <FoundingBadge user={user} />

        {/* Ecowitt soil moisture integration */}
        <EcowittSettingsCard />

        {/* Compliance Guard — opt-in local watering-ordinance enforcement */}
        <ComplianceGuardCard />

        {/* Vacation Mode — 7 AM local push digest */}
        <VacationModeCard />

        {/* Zone profiles — soil/slope/nozzle/etc. completeness editor */}
        <Card data-testid="zone-profiles-link-card" className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100">Zone profiles</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-400 leading-snug mb-3">
              Soil, slope, nozzle rate, root depth, area, and sun exposure power the
              Water Budget runtime math. Missing fields fall back to global defaults.
            </p>
            <Button
              data-testid="zone-profiles-link-btn"
              variant="outline"
              size="sm"
              onClick={() => navigate('/sensors/zone-profiles')}
            >
              Edit zone profiles
            </Button>
          </CardContent>
        </Card>

        {/* Biometric quick-unlock (Face ID / Touch ID / Windows Hello) */}
        <PasskeySettingsCard />

        {/* Super's Brief — daily 4:30 AM email preview + test-send */}
        <MorningBriefCard email={user?.email} tier={user?.subscription_tier} />

        {/* Personal API tokens — Home Assistant / n8n / external dashboards */}
        <ApiTokensCard />

        {/* Outbound webhooks — Inner Circle / paid feature */}
        <WebhooksCard />

        {/* Inner Circle upsell — only for non-admin, non-founding users */}
        {user?.role !== 'admin' && user?.subscription_tier !== 'founding' && (
          <Card className="border-emerald-500/40 bg-emerald-500/5" data-testid="profile-inner-circle-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Crown className="w-5 h-5 text-emerald-600" weight="duotone" />
                Become a Founding Member
              </CardTitle>
              <CardDescription>
                Limited to the first 10 seats · Free through Oct 31, 2026 · then $4.99/mo locked for life. You'll unlock outbound webhooks, 365-day depletion history, 10 API tokens, and the founding-member crest on your dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InnerCircleCTA testId="profile-inner-circle-cta" size="default" />
            </CardContent>
          </Card>
        )}

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('profile:accountInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border/50">
              <span className="text-sm text-muted-foreground">{t('profile:name')}</span>
              <span className="font-medium" data-testid="profile-name">{user?.name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border/50">
              <span className="text-sm text-muted-foreground">{t('profile:email')}</span>
              <span className="font-medium" data-testid="profile-email">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border/50">
              <span className="text-sm text-muted-foreground">{t('profile:role')}</span>
              <span className="font-medium capitalize" data-testid="profile-role">{user?.role}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border/50">
              <span className="text-sm text-muted-foreground">{t('profile:grassType')}</span>
              <span className="font-medium capitalize" data-testid="profile-grass">{user?.grass_type}</span>
            </div>
          </CardContent>
        </Card>

        {/* Privacy — analytics opt-out */}
        <Card data-testid="privacy-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck weight="duotone" size={20} />
              Privacy
            </CardTitle>
            <CardDescription>
              Help us understand which features help new users most. Your choice, anytime.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="font-medium">Help improve TurfPulse with anonymous page analytics</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  We record only page slug, session id (resets per tab), and how long
                  you stayed — no IP, no cookies, no clicks, no content. Retained 90
                  days. Turning this off also purges any existing data linked to your account.
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer shrink-0" data-testid="analytics-opt-out-toggle">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!analyticsOptOut}
                  disabled={analyticsLoading}
                  onChange={(e) => handleAnalyticsToggle(!e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/30 transition-colors">
                  <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard weight="duotone" size={20} />
              {t('profile:subscription.title')}
            </CardTitle>
            <CardDescription>{t('profile:subscription.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-border/50">
              <span className="text-sm text-muted-foreground">{t('profile:subscription.plan')}</span>
              <div className="flex items-center gap-2">
                <span className={`font-medium ${isPremium ? 'text-green-600 dark:text-green-400' : ''}`} data-testid="profile-tier">
                  {tierDisplay}
                </span>
                {isCanceling && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
                    {t('profile:subscription.canceling')}
                  </span>
                )}
              </div>
            </div>
            
            {subscriptionDetails?.current_period_end && (
              <div className="flex justify-between items-center py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <CalendarBlank size={14} />
                  {isCanceling ? t('profile:subscription.accessUntil') : t('profile:subscription.nextBilling')}
                </span>
                <span className="font-medium">
                  {new Date(subscriptionDetails.current_period_end).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </span>
              </div>
            )}
            
            {subscriptionDetails?.auto_renew !== undefined && (
              <div className="flex justify-between items-center py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <ArrowsClockwise size={14} />
                  Auto-Renew
                </span>
                <span className={`font-medium ${subscriptionDetails.auto_renew ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {subscriptionDetails.auto_renew ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-2 space-y-3">
              {/* Free users - show subscribe button */}
              {!isPremium && (
                <Button 
                  onClick={() => setShowUpgradeDialog(true)}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="subscribe-btn"
                >
                  <ArrowUp size={16} className="mr-2" />
                  Subscribe to Enthusiast Annual - $80/year
                </Button>
              )}
              
              {/* Homeowner Seasonal users - show upgrade button */}
              {user?.subscription_tier === 'homeowner_seasonal' && !isCanceling && (
                <Button 
                  onClick={() => setShowUpgradeDialog(true)}
                  className="w-full"
                  variant="outline"
                  data-testid="upgrade-to-annual-btn"
                >
                  <ArrowUp size={16} className="mr-2" />
                  Upgrade to Enthusiast Annual
                </Button>
              )}
              
              {/* Premium users with Stripe subscription - show management options */}
              {isPremium && subscriptionDetails?.source === 'stripe_subscription' && (
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Manage Billing Portal */}
                  <Button 
                    onClick={handleOpenPortal}
                    variant="outline"
                    className="flex-1"
                    disabled={portalLoading}
                    data-testid="billing-portal-btn"
                  >
                    <Receipt size={16} className="mr-2" />
                    {portalLoading ? 'Opening...' : 'Billing & Invoices'}
                  </Button>
                  
                  {/* Cancel or Reactivate */}
                  {isCanceling ? (
                    <Button 
                      onClick={handleReactivateSubscription}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={upgradeLoading}
                      data-testid="reactivate-btn"
                    >
                      <ArrowsClockwise size={16} className="mr-2" />
                      {upgradeLoading ? 'Processing...' : 'Reactivate'}
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => setShowCancelDialog(true)}
                      variant="ghost"
                      className="flex-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid="cancel-subscription-btn"
                    >
                      <XCircle size={16} className="mr-2" />
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              )}
              
              {/* Legacy one-time payment users */}
              {isPremium && subscriptionDetails?.source !== 'stripe_subscription' && subscriptionDetails?.expires && (
                <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                  <p className="mb-2">
                    Your current plan was purchased as a one-time payment and expires on{' '}
                    <strong>{new Date(subscriptionDetails.expires).toLocaleDateString()}</strong>.
                  </p>
                  <Button 
                    onClick={() => setShowUpgradeDialog(true)}
                    size="sm"
                    variant="outline"
                    data-testid="switch-to-subscription-btn"
                  >
                    Switch to Auto-Renewing Subscription
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Pro / Business Upsell — shown to anyone NOT already on Pro ── */}
        {user?.subscription_tier !== 'pro_business' && user?.role !== 'admin' && (
          <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" data-testid="pro-upsell-card">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightning size={18} weight="fill" className="text-primary" />
                    TurfPulse Pro
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Run your lawn business like the pros do — from your truck.
                  </CardDescription>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-primary text-primary-foreground">
                  For Contractors
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Measure any lawn from the satellite. Quote it in 60 seconds. Hand over a branded PDF before the next truck pulls in. All the turf science you already trust — bundled with the contractor tools you need.
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  'Property Measure & polygon drawing',
                  'One-tap AI satellite zone scan',
                  'Branded quote PDFs (EKG watermark)',
                  'Unlimited client properties',
                  'Featured partner listing',
                  'White-label contractor sheets',
                ].map((f) => (
                  <div key={f} className="flex items-start gap-2 text-xs text-foreground/80">
                    <CheckCircle size={14} weight="fill" className="shrink-0 mt-0.5 text-primary" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => window.location.assign('/pro')}
                className="w-full gap-2"
                data-testid="profile-pro-cta"
              >
                <Lightning size={16} weight="fill" />
                See what Pro unlocks
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Annual subscription · Cancel anytime · Secure checkout by Stripe
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pro users get a quick-access shortcut instead */}
        {(user?.subscription_tier === 'pro_business' || user?.role === 'admin') && (
          <Card className="border-2 border-primary/40 bg-primary/5" data-testid="pro-active-card">
            <CardContent className="py-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
                    <Lightning size={20} weight="fill" className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">TurfPulse Pro active</p>
                    <p className="text-xs text-muted-foreground">Open your contractor toolkit</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => window.location.assign('/clients')}
                    size="sm"
                    variant="outline"
                    data-testid="profile-pro-clients-btn"
                  >
                    Clients
                  </Button>
                  <Button
                    onClick={() => window.location.assign('/measure')}
                    size="sm"
                    data-testid="profile-pro-measure-btn"
                  >
                    Property Measure →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock weight="duotone" size={20} />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">{t('profile:changePassword.currentPassword')}</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    data-testid="current-password-input"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    placeholder={t('profile:changePassword.currentPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showCurrent ? 'Hide password' : 'Show password'}
                  >
                    {showCurrent ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">{t('profile:changePassword.newPassword')}</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    data-testid="new-password-input"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={4}
                    placeholder={t('profile:changePassword.newPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showNew ? 'Hide password' : 'Show password'}
                  >
                    {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('profile:changePassword.confirmPassword')}</Label>
                <Input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder={t('profile:changePassword.confirmPassword')}
                />
                {confirmPassword && newPassword && (
                  <p className={`text-xs ${newPassword === confirmPassword ? 'text-green-600' : 'text-destructive'}`}>
                    {newPassword === confirmPassword ? (
                      <span className="flex items-center gap-1"><CheckCircle size={14} /> {t('profile:changePassword.success', 'Passwords match')}</span>
                    ) : t('profile:changePassword.mismatch')}
                  </p>
                )}
              </div>

              <Button
                data-testid="change-password-btn"
                type="submit"
                disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                className="w-full"
              >
                {loading ? t('common:loading') : t('profile:changePassword.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Upgrade Dialog */}
      <UpgradeDialog 
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        onConfirm={handleSubscribe}
        loading={upgradeLoading}
        currentTier={user?.subscription_tier}
      />

      {/* Cancel Subscription Dialog */}
      <CancelDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelSubscription}
        loading={cancelLoading}
        expiresDate={subscriptionDetails?.current_period_end}
      />
    </div>
  );
};
